// Per-dialect DDL synthesizers. v1 covers ALTER TABLE … ADD/DROP/RENAME COLUMN
// across the 5-engine matrix. The applicator picks the right helper based on
// `ParsedBuffer.parsedDocument.options.engine` and calls one synthesizer per
// edit op.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Column Edit Applicator (DSE3
// v1 op set, DSE4 dialect dispatch lives here, DSE6 quoting style preserved).
//
// MSSQL rename uses `sp_rename` because mssql has no `ALTER TABLE … RENAME
// COLUMN` syntax. The other four dialects share `ALTER TABLE … RENAME COLUMN
// <old> TO <new>` (Oracle, Postgres, MySQL 8+, SQLite 3.25+).

import type { IdentifierQuoting, QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { ColumnDef } from '../../types';

import { formatCollationSpec } from './dialect.collation';
import { formatGeneratedSpec } from './dialect.generated';
import { formatIdentitySpec } from './dialect.identity';
import {
  type ExistingNameOrigin,
  inferQuoting,
  nameExistingColumn,
  needsQuoting,
  quoteAsOwner,
  quoteWithStyle,
} from './dialect.quoting';

const QUOTE_OPEN: Record<SqlEngine, string> = {
  mssql: '[', postgres: '"', mysql: '`', oracle: '"', sqlite: '"',
};
const QUOTE_CLOSE: Record<SqlEngine, string> = {
  mssql: ']', postgres: '"', mysql: '`', oracle: '"', sqlite: '"',
};

/** Wrap `name` in the engine's default quoting characters. Used when the
 *  source table identifier was quoted — DSE6 says match that style for new
 *  columns. */
function quoteIdent(engine: SqlEngine, name: string): string {
  const close = QUOTE_CLOSE[engine];
  return `${QUOTE_OPEN[engine]}${name.split(close).join(close + close)}${close}`;
}

/** Quote `name` only when `shouldQuote` is true. v2 Slice H2: pass `owner`
 *  to use the owner's source quoting style (per-token preservation, e.g.
 *  `[Foo]` vs `"Foo"` on mssql). When omitted, falls back to engine default
 *  (v1 posture). */
export function maybeQuoteIdent(
  engine: SqlEngine,
  name: string,
  shouldQuote: boolean,
  owner?: QualifiedName,
): string {
  if (!shouldQuote) return name;
  if (owner !== undefined) {
    const style = inferQuoting(owner, engine);
    if (style !== 'unquoted') return quoteWithStyle(name, style);
    // Owner is unquoted but caller asked us to quote — use engine default.
  }
  return quoteIdent(engine, name);
}

/** Wrap `value` as a single-quoted SQL string literal, escaping embedded
 *  single quotes by doubling them (`'` → `''`). Every supported engine
 *  (mssql/postgres/mysql/oracle/sqlite) accepts doubled-quote escaping in a
 *  standard `'...'` literal. Use this for ANY user-supplied value emitted as a
 *  quoted literal (passwords, file paths, principal host/name, encodings) so a
 *  value containing `'` cannot break out of the literal or inject SQL. */
export function quoteSqlLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

export { inferQuoting, quoteAsOwner, quoteWithStyle };

/** Render the column body shared by CREATE TABLE column-list inserts and
 *  ALTER TABLE ADD COLUMN clauses: `<name> <typeRef> [<collation>]
 *  [<identity>] [<generated>] [NOT NULL|NULL] [DEFAULT <expr>]` — except
 *  oracle, whose grammar requires DEFAULT before the inline constraint:
 *  `… [DEFAULT <expr>] [NOT NULL|NULL]`. The
 *  collation clause sits immediately after the type because every supported
 *  engine accepts (and the reference manuals favor) that ordering. The
 *  identity clause goes after collation. v2 Slice A2 promotes
 *  `column.identity` to a structured `IdentitySpec`. v2 Slice A3 adds
 *  generated columns: emitted between identity and nullability for every
 *  engine except mssql, whose computed-column syntax is
 *  `<name> AS (<expr>) [PERSISTED]` with no type position — when mssql +
 *  generated, the typeRef is omitted (callers may still pass a typeRef
 *  field; it is ignored on the mssql generated path). v2 Slice A4 adds
 *  collation; on the mssql generated path collation is rejected because
 *  computed columns inherit collation from the source expression. */
export function formatColumnBody(
  engine: SqlEngine,
  column: ColumnDef,
  quoteName: boolean,
  owner?: QualifiedName,
): string {
  const name = maybeQuoteIdent(engine, column.name, quoteName, owner);
  if (engine === 'mssql' && column.generated !== undefined) {
    if (column.collation !== undefined) {
      throw new Error(
        `applyDdlEdit: mssql computed columns inherit collation from the `
          + `source expression — drop 'collation' from a generated ColumnDef`,
      );
    }
    let mssqlGen = `${name} ${formatGeneratedSpec(engine, column.generated)}`;
    if (column.nullable === false) mssqlGen += ' NOT NULL';
    else if (column.nullable === true) mssqlGen += ' NULL';
    return mssqlGen;
  }
  let s = `${name} ${column.typeRef}`;
  if (column.collation !== undefined) {
    s += ` ${formatCollationSpec(engine, column.collation)}`;
  }
  if (column.identity !== undefined) {
    s += ` ${formatIdentitySpec(engine, column.identity)}`;
  }
  if (column.generated !== undefined) {
    s += ` ${formatGeneratedSpec(engine, column.generated)}`;
  }
  // Oracle's column grammar is `datatype [DEFAULT expr] [inline_constraint…]`:
  // DEFAULT must PRECEDE the NOT NULL/NULL constraint or the engine rejects the
  // DDL (ORA-30649 on `… NOT NULL DEFAULT 0`, verified live). Matches the
  // ordering `dialect.alterColumn.ts` already uses for oracle MODIFY. The other
  // four engines keep the documented `[NOT NULL|NULL] [DEFAULT]` order.
  if (engine === 'oracle' && column.defaultText !== undefined) {
    s += ` DEFAULT ${column.defaultText}`;
  }
  if (column.nullable === false) s += ' NOT NULL';
  else if (column.nullable === true) s += ' NULL';
  if (engine !== 'oracle' && column.defaultText !== undefined) {
    s += ` DEFAULT ${column.defaultText}`;
  }
  return s;
}

/** Use the qualified name's verbatim raw text when present so the source's
 *  quoting / casing carries through. Falls back to a synthesized form for
 *  the rare case the QualifiedName was constructed without raw bytes.
 *  Exported so the alter-table coalescer (slice H1) can compute the exact
 *  `ALTER TABLE <prefix> ` prefix used by every per-op-family synthesizer. */
export function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

/** ALTER TABLE … ADD [COLUMN] <colDef>. mssql + oracle drop the COLUMN
 *  keyword by convention; the other three keep it. */
export function synthesizeAlterAddColumn(
  engine: SqlEngine,
  table: QualifiedName,
  column: ColumnDef,
): string {
  const body = formatColumnBody(engine, column, table.quoted, table);
  if (engine === 'mssql' || engine === 'oracle') {
    return `ALTER TABLE ${tableText(engine, table)} ADD ${body}`;
  }
  return `ALTER TABLE ${tableText(engine, table)} ADD COLUMN ${body}`;
}

/** ALTER TABLE … DROP COLUMN <name>. Identical across the 5 engines (sqlite
 *  3.35.0+; the round-trip corpus slice gates by available engine version).
 *
 *  `columnName` names a column the catalog ALREADY holds, so it is read under
 *  the same three-way contract as the rename's old name — see
 *  `dialect.quoting.ts`'s {@link ExistingNameOrigin}. `quoteName` is only the
 *  DSE6 style flag, and a style may not decide identity: dropping a bare
 *  `Target_Col` under a QUOTED host has to ask for `"target_col"` on
 *  PostgreSQL, not for the typed bytes the fold never created. */
export function synthesizeAlterDropColumn(
  engine: SqlEngine,
  table: QualifiedName,
  columnName: string,
  quoteName: boolean,
  columnNameOrigin: ExistingNameOrigin = 'bare-reference',
): string {
  const { text, delimit } = nameExistingColumn(columnName, engine, columnNameOrigin, quoteName);
  const col = maybeQuoteIdent(engine, text, delimit, table);
  return `ALTER TABLE ${tableText(engine, table)} DROP COLUMN ${col}`;
}

/** ALTER TABLE … RENAME COLUMN <old> TO <new> for postgres / mysql / oracle /
 *  sqlite. mssql has no equivalent ALTER syntax — emits `EXEC sp_rename`
 *  with the table-qualified source identifier per R2.
 *
 *  R2 quoting note: sp_rename's first argument is a string literal that
 *  parser-quotes inside SQL; the table reference inside it uses normal
 *  identifier quoting. We reuse the source's `raw` text for the table to
 *  preserve schema / quoting style. Both names ride inside string literals
 *  there, so a name that needs delimiters as an identifier needs none here.
 *
 *  `quoteNames` is a STYLE preference (DSE6: match the source's quoting), and a
 *  name that cannot stand unquoted has no unquoted spelling to prefer — so it
 *  is delimited whatever the flag says, per component, and "cannot stand
 *  unquoted" is asked OF THIS ENGINE: `#` is a bare-identifier character on
 *  SQL Server and Oracle and a parse error on the other three. Without that,
 *  `quoteNames === false` emitted the invalid
 *  `… RENAME COLUMN id TO user id`. `rename/terminalTokenEdit.ts`'s
 *  `destinationCatalogName` already assumes exactly this (an unquotable name
 *  never folds, because only a delimited ALTER can create it), so the emitted
 *  statement and every rewritten body/tab now agree.
 *
 *  `oldNameOrigin` says WHERE `oldName` came from, which is the only thing that
 *  settles whether this engine's fold RESOLVES the name or CORRUPTS it — a
 *  question the string itself cannot answer. `dialect.quoting.ts`'s
 *  {@link ExistingNameOrigin} holds the three origins; the rule each implies
 *  here:
 *
 *    • A REFERENCE THE SOURCE WROTE BARE (`'bare-reference'`, the default, and
 *      what `ddl/apply/columnEdit.alterTable.ts` passes for a column its own
 *      source statement does not spell delimited): a spelling that NAMES the
 *      column once this engine has folded it. An Oracle script's
 *      `RENAME COLUMN target` finds the catalog's `TARGET`, and delimiting it
 *      would ask for a column that does not exist (ORA-00904). Proven live by
 *      the round-trip corpus entry `rename-column-alter-table`, which seeds
 *      `target` bare and executes the emitted statement.
 *
 *      So when this statement DOES delimit such a name — for style, because
 *      the host is quoted, or because the spelling has no bare form here — the
 *      delimiters go around `bareReferenceIdentity`, the spelling the bare
 *      token named, NOT the typed bytes. Delimiters suppress the fold that was
 *      resolving the reference, so `RENAME COLUMN "Target_Col"` under a quoted
 *      host asks PostgreSQL for a column no fold ever created while the bare
 *      `Target_Col` it came from found `target_col`.
 *
 *      That is also what makes the two EXACT reserved lists safe across server
 *      versions. Each is one word too wide before its floor (PostgreSQL
 *      reserved SYSTEM_USER in 16, Oracle EXCEPT in 21c), so a script written
 *      against PostgreSQL 15 / Oracle 18c holds a bare `System_User` /
 *      `except` that this emitter delimits on a server that would have read it
 *      fine. Delimiting the FOLD makes that harmless: `"system_user"` /
 *      `"EXCEPT"` is the column the bare token named on every version, so the
 *      reservation question stops deciding the identity and only decides
 *      whether delimiters appear. Selecting a list by server version would fix
 *      the same gap and costs version plumbing through five files
 *      (`ParseOptions.serverVersion` is never populated in production); this
 *      does not need the version at all.
 *    • BYTES THE SERVER ALREADY HOLDS, reached from either end:
 *      `'catalog-spelling'`, which the nav rename passes because
 *      `go-to/columnAtCursor.ts` binds the tree's own spelling, or
 *      `'delimited-source'`, which `columnEdit.alterTable.ts` passes when the
 *      SOURCE STATEMENT spelled that column delimited — `ALTER TABLE "users"
 *      ADD COLUMN "Id" integer` creates exactly `Id`, because delimiters
 *      suppressed the fold there too. Here a fold is not resolution but
 *      corruption, so `needsDelimitersToNameExactly` also refuses a spelling
 *      this engine's fold would change. A bare host sets
 *      `quoteNames === false`, so `SELECT users."Id" FROM users` emitted
 *      `… RENAME COLUMN Id TO …` — asking PostgreSQL for `id` and Oracle for
 *      `ID` (`column "id" does not exist` / ORA-00904); a quoted host with a
 *      folding default emitted `"id"` / `"ID"` for the same column.
 *
 *  The DESTINATION keeps the weaker test under every origin: there the fold
 *  IS the intended identity, and `rename/terminalTokenEdit.ts`'s
 *  `destinationCatalogIdentity` predicts exactly it, so the ALTER and every
 *  rewritten body agree that a bare `User_Id` becomes `user_id`.
 *
 *  `delimitDestinationAs` is the third reason to delimit, and it has TWO
 *  sources — it is an instruction, not provenance:
 *
 *    1. The user typed the destination WITH delimiters. Those come off exactly
 *       once, at the rename entry points (`dialect.quoting.ts`'s
 *       `decodeDelimitedIdentifier`), so `newName` here is always the
 *       identifier's own bytes and this argument is the surviving intent — put
 *       it back on ONCE. Letting the typed spelling through instead wrapped it
 *       a second time (a column whose NAME contains quote characters) while
 *       `destinationCatalogName` read that same string as already-delimited:
 *       two identifiers for one rename.
 *    2. The destination is itself a CATALOG spelling whose exact bytes must
 *       survive. Only an UNDO asks this: its target is the name the server held
 *       before the plan ran, so a bare `Id` would restore `id` / `ID` while the
 *       post-undo pass rewrites buffers back to `Id`. The caller passes the
 *       engine's own delimiters (`renameSchemaAlter.ts`).
 *
 *  Only whether it is set is read, never which style — a quoted host still
 *  wins its own. MSSQL needs no branch of its own: both names ride inside
 *  `sp_rename` string literals, where the decoded bytes are what the catalog
 *  stores. */
export function synthesizeAlterRenameColumn(
  engine: SqlEngine,
  table: QualifiedName,
  oldName: string,
  newName: string,
  quoteNames: boolean,
  delimitDestinationAs?: IdentifierQuoting,
  /** See the origins above. `'bare-reference'` (the default) keeps `oldName` a
   *  reference the engine's fold resolves; the other two say the bytes are the
   *  server's own, which a fold would change into a different column. */
  oldNameOrigin: ExistingNameOrigin = 'bare-reference',
): string {
  if (engine === 'mssql') {
    const tbl = tableText(engine, table);
    return `EXEC sp_rename '${tbl}.${oldName}', '${newName}', 'COLUMN'`;
  }
  const delimitDestination =
    delimitDestinationAs !== undefined && delimitDestinationAs !== 'unquoted';
  // Adding delimiters to a BARE REFERENCE may not move it to another column:
  // the delimiters suppress the very fold that was resolving it, so what goes
  // inside them is the spelling the bare token named. Under the other two
  // origins there was never a fold to preserve — those bytes are already the
  // server's own. `nameExistingColumn` holds both halves of that decision.
  const { text: oldText, delimit: delimitOld } =
    nameExistingColumn(oldName, engine, oldNameOrigin, quoteNames);
  const oldCol = maybeQuoteIdent(engine, oldText, delimitOld, table);
  const newCol = maybeQuoteIdent(
    engine,
    newName,
    quoteNames || needsQuoting(newName, engine) || delimitDestination,
    table,
  );
  return `ALTER TABLE ${tableText(engine, table)} RENAME COLUMN ${oldCol} TO ${newCol}`;
}
